import React, { useEffect, useState, useMemo, useRef } from "react";
import { ActionIcon, Button, Collapse, Group, Stack, Table, Tooltip } from "@mantine/core";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getMasterTrades } from "app/state/ducks/master-trades/selectors";
import { MasterTradeParams } from "app/models/master-trade-params";
import { IconChevronDown, IconChevronRight, IconInfoCircle } from "@tabler/icons-react";
import { getLockedFacilities } from "app/state/ducks/facilities/selectors";
import { getEntityProfiles } from "app/state/ducks/entity-profile/selectors";
import { formatPriceWithoutCurrency, isFundManagerEntity } from "app/utils/util-functions";
import { facilityStatusInfo } from "../../trade-enty-page-columnDefs";
import { FacilityAllocationHandlers, SubTradeParams } from "app/models/sub-trade-params";
import { getSubTrades } from "app/state/ducks/sub-trades/selectors";
import SubTradeEntryPanel from "./sub-trade-entry-panel";
import { getTradedFacilities } from "app/state/ducks/traded-facilities/selectors";
import { getFacilityAllocationAction } from "./sub-trade-utils";
import { useTradePageDeals, useTradePageEntities, useTradePageFacilities } from "app/views/pages/trade-entry/detail-trade/use-trade-page-lookups";

const PRORATE_TOOLTIP =
  'Prorate evenly splits the remaining amount across the entities with no amount allocated yet.'
const UNALLOCATE_TOOLTIP =
  'Unallocate clears every entity amount for this facility, resetting them to 0.'


export function SubTradeEntry() {
  const { tradeId } = useParams();
  const allMasterTrades = useSelector(getMasterTrades);
  const deals = useTradePageDeals();
  const facilities = useTradePageFacilities();
  const subTrades: SubTradeParams[] = useSelector(getSubTrades)
  const allTradedFacilities = useSelector(getTradedFacilities)
  const entities = useTradePageEntities()
  const entityProfiles = useSelector(getEntityProfiles)
  const isInitialMount = useRef(true);
  const lockedFacilities = useSelector(getLockedFacilities)
  const allFacilities = useMemo(() => [...facilities, ...lockedFacilities], [facilities, lockedFacilities]);

  const [masterTrade, setMasterTrade] = useState<MasterTradeParams>();
  const [opened, setOpened] = useState(false);
  // Prorate/Unallocate handlers registered by the Update Allocations editor
  // while it is open; null when it is closed (the buttons are hidden then).
  const [facilityActionHandlers, setFacilityActionHandlers] = useState<FacilityAllocationHandlers | null>(null);

  // Find Master Trade
  useEffect(() => {
    if (!tradeId || !allMasterTrades.length) return;
    const trade = allMasterTrades.find((trade) => trade.id === tradeId);
    setMasterTrade(trade);
  }, [tradeId, allMasterTrades]);

  // Filter Traded Facilities
  const tradedFacilities = useMemo(() => {
    if (!tradeId || !allTradedFacilities.length) return [];
    return allTradedFacilities
      .filter((tf) => tf.contractId?.id === tradeId)
      .map((facility) => ({
        ...facility,
        facilityDetails: allFacilities.find((f) => f.id === facility.facilityId.id) ?? null,
      }));
  }, [tradeId, allTradedFacilities, allFacilities]);
  // Calculate Allocated Amounts
  const totalAllocatedAmount = useMemo(() => {
    if (!tradeId || !allTradedFacilities.length) return new Map<string, number>();

    const filteredTradeIds = subTrades
      .filter((trade) => trade.masterTradeId.id === tradeId)
      .map((trade) => trade.id);

    return allTradedFacilities
      .filter((tf) => filteredTradeIds.includes(tf.contractId?.id))
      .reduce((acc: Map<string, number>, tf) => {
        const facilityId = tf.facilityId.id;
        const tradeAmount = Number(tf.tradeAmount);
        acc.set(facilityId, (acc.get(facilityId) || 0) + tradeAmount);
        return acc;
      }, new Map<string, number>());
  }, [tradeId, allTradedFacilities, subTrades]);

  useEffect(() => {
    if (isInitialMount.current && tradedFacilities.length > 0) {
      const unallocatedFacility = tradedFacilities.find(e =>
        Number(e.tradeAmount) - (totalAllocatedAmount.get(e.facilityId.id) ?? 0) > 0
      );
      if (unallocatedFacility) {
        setOpened(true);
        isInitialMount.current = false;
      }
    }

  }, [tradedFacilities, totalAllocatedAmount]);


  // Use a ref flag to ensure the update happens only once
  const hasUpdatedMasterTrade = useRef(false);

  useEffect(() => {
    if (!masterTrade || !deals.length || hasUpdatedMasterTrade.current) return;

    const matchedDeal = deals.find(
      (deal) => deal.id === masterTrade.dealId?.id
    );
    if (!matchedDeal) return;

    setMasterTrade((prevMasterTrade) => {
      if (!prevMasterTrade) return undefined;

      // Check if the masterTrade already has the updated deal details
      if (
        prevMasterTrade.dealCusip === matchedDeal.cusip &&
        prevMasterTrade.dealName === matchedDeal.dealName &&
        prevMasterTrade.dealId?.id === matchedDeal.id
      ) {
        return prevMasterTrade;
      }

      // Set the flag to avoid future updates
      hasUpdatedMasterTrade.current = true;
      return {
        ...prevMasterTrade,
        dealCusip: matchedDeal.cusip ?? "",
        dealName: matchedDeal.dealName ?? "",
        dealId: { id: matchedDeal.id, admin: matchedDeal.accountManagementAdmin },
      };
    });
  }, [masterTrade, deals]);

  const isSellerFundManager = isFundManagerEntity(masterTrade?.sellerId?.id, entities, entityProfiles);
  const isBuyerFundManager = isFundManagerEntity(masterTrade?.buyerId?.id, entities, entityProfiles);
  return (
    <Stack w="100%">
      <div className="stack-container" style={{ width: '100%' }}>
        {((isSellerFundManager || isBuyerFundManager) && masterTrade?.status !== "Settled") &&
          <div className="panel-box">
            <div className={`panel-header ${opened ? 'panel-header-open' : ''}`} onClick={() => setOpened((prev) => !prev)}>
              <ActionIcon variant="transparent">
                {opened ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
              </ActionIcon>
              <div className="panel-title">To Be Allocated</div>
            </div>

            <Collapse in={opened} style={{ width: '100%' }}>
              {opened && (
                <div className="table-container">
                  <Table
                    highlightOnHover
                    striped={false}
                    className="remove-stripe-table"
                    style={{ width: '100% !important' }}
                  >
                    <thead style={{ backgroundColor: '#F0EEEE' }}>
                      <tr className="table-header">
                        <th>Facilities</th>
                        <th>Status</th>
                        <th>Total Traded</th>
                        <th>Total Allocated</th>
                        <th>Remaining</th>
                        {facilityActionHandlers && <th></th>}
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {tradedFacilities
                        .slice()
                        .sort((a, b) => {
                          const nameA = a.facilityDetails?.name?.toLowerCase() || ''
                          const nameB = b.facilityDetails?.name?.toLowerCase() || ''
                          return nameA.localeCompare(nameB)
                        })
                        .map((e, index) => (
                          <tr
                            key={`${e.facilityId?.id}-${e.tradeAmount}-${index}`}
                            className="table-row"
                            onClick={() => console.log("clicked")}
                          >
                            <td className="table-cell">
                              {e.facilityDetails
                                ? `${e.facilityDetails.name ?? ""}${e.facilityDetails.cusip
                                  ? ` (${e.facilityDetails.cusip})`
                                  : ""
                                }`
                                : ""}
                            </td>
                            {(() => {
                              const info = facilityStatusInfo[e.facilityDetails?.status ?? '']
                              return (
                                <td
                                  className="table-cell"
                                  style={{
                                    color: info?.color,
                                    fontWeight: 600,
                                    textAlign: 'right',
                                  }}
                                >
                                  {info?.display ?? ''}
                                </td>
                              )
                            })()}
                            <td className="table-cell number">
                              {formatPriceWithoutCurrency(Number(e.tradeAmount), 2, 2) ?? ""}
                            </td>
                            <td className="table-cell number">
                              {formatPriceWithoutCurrency(
                                Number(totalAllocatedAmount.get(e.facilityId.id) ?? 0),
                                2,
                                2
                              )}
                            </td>
                            {(() => {
                              const remaining = Math.round((Number(e.tradeAmount) - Number(totalAllocatedAmount.get(e.facilityId.id) ?? 0)) * 100) / 100 || 0
                              return (
                                <td
                                  className="table-cell number remaining"
                                  style={{
                                    color: remaining < 0 ? '#EE5959' : undefined
                                  }}
                                >
                                  {formatPriceWithoutCurrency(remaining, 2, 2)}
                                </td>
                              )
                            })()}
                            {facilityActionHandlers && (() => {
                              const allocated = Number(totalAllocatedAmount.get(e.facilityId.id) ?? 0)
                              const action = getFacilityAllocationAction(
                                Number(e.tradeAmount),
                                allocated,
                                facilityActionHandlers.editableFacilityIds.has(e.facilityId.id),
                              )
                              const isProrate = action === 'prorate'
                              return (
                                <td className="table-cell" onClick={(event) => event.stopPropagation()}>
                                  {action && (
                                    <Group spacing={6} noWrap style={{ marginLeft: 'clamp(10px, 2vw, 40px)' }}>
                                      <Button
                                        className={isProrate ? 'allocation-prorate-button' : 'allocation-unallocate-button'}
                                        classNames={{ label: 'allocation-facility-action-button-text' }}
                                        onClick={() =>
                                          isProrate
                                            ? facilityActionHandlers.onProrate(e.facilityId.id)
                                            : facilityActionHandlers.onUnallocate(e.facilityId.id)
                                        }
                                      >
                                        {isProrate ? 'Prorate' : 'Unallocate'}
                                      </Button>
                                      <Tooltip
                                        label={isProrate ? PRORATE_TOOLTIP : UNALLOCATE_TOOLTIP}
                                        multiline
                                        width={280}
                                        withinPortal
                                        events={{ hover: true, focus: true, touch: false }}
                                      >
                                        <ActionIcon
                                          variant="transparent"
                                          radius="xl"
                                          aria-label={isProrate ? 'About Prorate' : 'About Unallocate'}
                                        >
                                          <IconInfoCircle size={20} style={{ color: '#476DB9' }} />
                                        </ActionIcon>
                                      </Tooltip>
                                    </Group>
                                  )}
                                </td>
                              )
                            })()}
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Collapse>
          </div>}
        {masterTrade && <SubTradeEntryPanel row={masterTrade} onRegisterFacilityActions={setFacilityActionHandlers} />}
      </div>
    </Stack>
  );
}
